<template>
  <v-layout>

    <info-dialog v-model="showInfoDialog"
                 :invoicing-company="invoicingCompany">
    </info-dialog>

    <v-dialog v-model="showBillableDialog"
              :full-width="!$vuetify.breakpoint.smAndDown"
              max-width="500">
      <v-card class="pa-4">
        <div class="text--primary">
          <ul>
            <li>Export a {{ $t('terms.billable-expense') }} from a {{ $t('terms.vendor') }}</li>
            <li>Assign the {{ $t('terms.billable-expense') }} to a customer</li>
            <li>Provide the expense account line item for the bill</li>
            <li>Optionally add income items such as equipment usage or dispatch deductions</li>
            <li v-if="isUsingTax">Specify Tax Liability / Withholding</li>
          </ul>
        </div>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showTransactionHistory"
              :full-width="!$vuetify.breakpoint.smAndDown"
              max-width="500">
      <v-card class="pa-4">
        <v-load-transaction-histories :company="invoicingCompany" :transaction="transaction">
        </v-load-transaction-histories>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn flat @click="showTransactionHistory = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showCustomerDialog"
              :full-width="!$vuetify.breakpoint.smAndDown"
              max-width="500">
      <v-card class="pa-4">
        <div class="text--primary">
          <ul>
            <li>Create an invoice against the customer/dispatcher</li>
            <li>Provide the income account line item</li>
            <li>Optionally add internal expenses such as dispatch or load withholding</li>
            <li v-if="isUsingTax">Specify Tax Liability / Withholding</li>
          </ul>
        </div>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showInvoiceDialog"
              persistent
              lazy
              @input="v => v"
              max-width="700">
      <v-card :class="{'pa-4': !$vuetify.breakpoint.smAndDown}">
        <v-layout column>
          <v-card>
            <v-toolbar>
              <img class="align-center cursor-pointer"
                   height="38" width="38"
                   src="/static/Quickbooks-Logo.png"/>
              <v-toolbar-title>{{ quickbooksExportTitle }}</v-toolbar-title>
            </v-toolbar>
            <v-list>
              <v-list-tile class="pl-2">
                <v-list-tile-content>
                  <v-list-tile-title>Invoiced to: {{ txnCustomerCompanyName }}</v-list-tile-title>
                  <v-list-tile-sub-title>Paid to: {{ txnVendorCompanyName }}</v-list-tile-sub-title>
                </v-list-tile-content>
              </v-list-tile>
              <v-list-tile v-if="billOfLadingNumber" class="pl-2">
                <v-list-tile-content>
                  <v-list-tile-title>
                    {{ $t('terms.bill-of-lading') }} Number: {{billOfLadingNumber}}
                  </v-list-tile-title>
                  <v-list-tile-sub-title>
                    <v-btn class="primary--text ma-0 px-0" flat
                           @click="openBillOfLading()"
                           v-if="_.get(transaction, 'load.billOfLading.link', false)">
                      <v-icon color="primary">attachment</v-icon>
                      View {{ $t('terms.bill-of-lading') }}
                    </v-btn>
                  </v-list-tile-sub-title>
                </v-list-tile-content>
              </v-list-tile>
              <v-list-tile v-if="exportQuickbooksStep" class="pl-2">
                <v-list-tile-content>
                  <v-list-tile-sub-title>
                    <v-btn class="primary--text ma-0 px-0" flat
                           @click="showTransactionHistory = true">
                      <v-icon color="primary">timeline</v-icon>
                      History
                    </v-btn>
                  </v-list-tile-sub-title>
                </v-list-tile-content>
              </v-list-tile>
            </v-list>
          </v-card>
          <v-alert type="warning"
                   v-if="isQuickbooksConnected(invoicingCompany)"
                   :value="daysUntilQuickbooksCredentialsExpire(invoicingCompany) < 8">
            <v-layout row wrap>
              <v-flex xs8 v-if=isQuickbooksConnected(invoicingCompany)>
                <span>Your Quickbooks Credentials expire in {{ daysUntilQuickbooksCredentialsExpire(invoicingCompany) }} days.</span>
              </v-flex>
              <v-flex xs4>
                <v-btn color="accent" :to="`/companies/${invoicingCompany.id}/profile#quickbooks`">
                  Fix It
                </v-btn>
              </v-flex>
            </v-layout>
          </v-alert>
          <v-spacer class="mb-3" />

          <v-card v-if="exportQuickbooksStep == null" pa-0 flat>
            <v-container
              fluid
              grid-list-lg
              class="px-0 pt-1">
              <v-layout row wrap>
                <v-flex xs12>
                  <v-card class="text--primary text--darken-2">
                    <v-toolbar class="text--white" color="primary " flat>
                      <v-toolbar-title class="white--text">
                        Billable Expense
                      </v-toolbar-title>
                    </v-toolbar>
                    <v-list-tile>
                      <v-list-tile-sub-title>
                        for dispatched loads
                        <v-btn color="primary"
                               @click="showBillableDialog = true"
                               fab small flat>
                          <v-icon>help_outline</v-icon>
                        </v-btn>
                      </v-list-tile-sub-title>
                    </v-list-tile>
                    <v-card-actions class="align-center">
                      <v-btn block color="accent" large @click="setupBillableExpenseFlow">
                        <v-icon large class="pr-2">receipt</v-icon>
                        Export {{ $t('terms.billable-expense') }}
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                </v-flex>

                <v-flex xs12>
                  <v-card>
                    <v-toolbar flat>
                      <v-toolbar-title>
                        Customer Invoice
                      </v-toolbar-title>
                    </v-toolbar>

                    <v-list-tile>
                      <v-list-tile-sub-title>
                        for direct invoicing
                        <v-btn color="primary"
                               @click="showCustomerDialog = true"
                               fab small flat>
                          <v-icon>help_outline</v-icon>
                        </v-btn>
                      </v-list-tile-sub-title>
                    </v-list-tile>
                    <v-card-actions class="align-center">
                      <v-btn block large
                             color="primary darken-1" @click="setupInvoiceFlow">
                        <v-icon large color="white" class="pr-2">monetization_on</v-icon>
                        Create Invoice
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                </v-flex>
              </v-layout>
            </v-container>
            <v-card-actions class="align-center">
              <v-btn color="accent"
                     flat large outline
                     @click="resetState">
                <v-icon large class="pr-2">cancel</v-icon>
                Cancel
              </v-btn>
            </v-card-actions>
          </v-card>
          <div v-else>
            <v-stepper v-model="exportQuickbooksStep">
              <v-stepper-header>
                <v-stepper-step step="1" :complete="exportQuickbooksStep > 1">Load</v-stepper-step>
                <v-stepper-step step="2" :complete="exportQuickbooksStep > 2">
                  <span v-if="createInvoice">Service</span>
                  <span v-else>Expense</span>
                </v-stepper-step>
                <v-stepper-step :step="exportQuickbooksDiscountsStep" :complete="exportQuickbooksStep > exportQuickbooksDiscountsStep" v-if="!createInvoice">
                  Discounts
                </v-stepper-step>
                <v-stepper-step :step="exportQuickbooksTaxStep" :complete="exportQuickbooksStep > exportQuickbooksTaxStep">
                  Tax
                </v-stepper-step>
              </v-stepper-header>

              <v-stepper-items>
                <v-stepper-content step="1">
                  <h3>Load's Customer</h3>
                  <span class="font-weight-light">
                  Select {{ $t('site-title') }} customer from your Quickbooks customers
                </span>
                  <v-list two-line>
                    <v-list-tile>
                      <v-autocomplete
                        :items="customers"
                        :loading="loadingCustomers"
                        label="Quickbooks Customer"
                        item-text="selectDisplay"
                        item-value="qbCompanyId"
                        v-model="qbCustomer"
                        return-object
                        :disabled="loadingCustomers"
                        :error-messages="errors.first('customer', 'step1')"
                        data-vv-as="Quickbooks Customer"
                        data-vv-name="customer"
                        data-vv-scope="step1"
                        v-validate="'required'">
                        <template slot="item" slot-scope="slotItems">
                          <v-layout column>
                            <div class="title mr-3 mt-1 primary--text">
                            <span v-if="customerCompanyName(slotItems.item)">
                              <v-icon color="primary" v-if="slotItems.item.qbCompanyId">business</v-icon>
                              <!-- Issue #379 -->
                              <v-icon color="transparent" v-else>add_circle</v-icon>
                              {{ customerCompanyName(slotItems.item) }}
                            </span>
                              <span v-else>
                              <v-icon color="primary">person_outline</v-icon>
                              {{ customerDisplayName(slotItems.item) }}
                            </span>
                            </div>
                            <div class="grey--text caption"
                                 v-if="customerCompanyName(slotItems.item) && customerContactName(slotItems.item)">
                              <v-icon color="primary">person_outline</v-icon>
                              <span>{{ customerContactName(slotItems.item) }}</span>
                              <span v-if="customerCompanyName(slotItems.item) !== customerDisplayName(slotItems.item)"
                                    class="font-italic font-weight-light">
                            {{ customerDisplayName(slotItems.item) }}
                          </span>
                            </div>

                          </v-layout>
                        </template>
                      </v-autocomplete>
                      <v-list-tile-avatar class="pl-3">
                        <v-btn fab small flat color="accent" :disabled="loadingCustomers">
                          <v-icon color="accent" @click="refreshQuickbooksCustomers(invoicingCompany.id)">refresh</v-icon>
                        </v-btn>
                      </v-list-tile-avatar>
                    </v-list-tile>
                  </v-list>

                  <h3>Full Invoiced Amount</h3>
                  <v-float-field label="Invoiced Amount ($)"
                                 class="pl-3"
                                 v-model="invoicedAmount"
                                 :error-messages="errors.first('amount', 'step1')"
                                 data-vv-as="Invoiced Amount"
                                 data-vv-name="amount"
                                 data-vv-scope="step1"
                                 v-validate="'required'"/>

                  <h3>Assign Document Number</h3>
                  <v-radio-group v-model="selectedInvoiceNumber" mandatory>
                    <v-radio v-for="(option, index) in invoiceOptions"
                             :key="'invoiceOp_'+index"
                             :label="option.label" :value="option.value"></v-radio>
                    <v-radio label="Custom" value="__other"></v-radio>
                  </v-radio-group>

                  <v-text-field label="Custom Document Number"
                                v-if="selectedInvoiceNumber === '__other'"
                                v-model="customInvoiceNumber"
                                :error-messages="errors.first('documentNumber', 'step1')"
                                data-vv-as="Document Number"
                                data-vv-name="documentNumber"
                                data-vv-scope="step1"
                                v-validate="'required'">
                  </v-text-field>

                  <v-spacer />

                  <v-btn block large
                         :disabled="errors.any('step1')"
                         @click="setupExportQuickbooksStep2"
                         :color="btnColor">
                    <v-icon large color="white" class="pr-2">
                      <template v-if="createInvoice">
                        account_balance
                      </template>
                      <template v-else>
                        credit_card
                      </template>
                    </v-icon>
                    <span v-if="createInvoice">Select Product or Service</span>
                    <span v-else>Select Expense Account</span>
                  </v-btn>

                  <v-btn block flat
                         :color="btnColor" @click="openQuickbooksModal">
                    Start Over
                  </v-btn>
                </v-stepper-content>

                <v-stepper-content step="2">

                  <v-list >
                    <v-list-tile>
                      <v-list-tile-content>
                        <v-list-tile-title class="font-weight-bold" v-if="createInvoice">Product or Service</v-list-tile-title>
                        <v-list-tile-sub-title v-if="createInvoice">invoice sales line item</v-list-tile-sub-title>
                        <v-list-tile-title class="font-weight-bold" v-if="!createInvoice">Expense Account</v-list-tile-title>
                        <v-list-tile-sub-title v-if="!createInvoice">account based expense line</v-list-tile-sub-title>
                      </v-list-tile-content>
                    </v-list-tile>

                    <v-list-tile class="pt-1" v-if="createInvoice">
                      <v-autocomplete
                        class="mt-2"
                        :items="salesItems"
                        :loading="loadingSalesItems"
                        :disabled="loadingSalesItems"
                        label="Quickbooks Sales Item"
                        item-text="description"
                        item-value="itemId"
                        v-model="qbSalesItem"
                        return-object
                        :error-messages="errors.first('salesItem', 'step2')"
                        data-vv-as="Quickbooks Sales Item"
                        data-vv-name="salesItem"
                        data-vv-scope="step2"
                        v-validate="'required'"
                      >
                      </v-autocomplete>
                      <v-list-tile-avatar class="pl-3">
                        <v-btn fab small flat color="accent" :disabled="loadingSalesItems">
                          <v-icon color="accent" @click="refreshQuickbooksSalesItems(invoicingCompany.id)">refresh</v-icon>
                        </v-btn>
                      </v-list-tile-avatar>
                    </v-list-tile>
                    <v-list-tile class="pt-1 pb-3" v-if="!createInvoice">
                      <v-autocomplete
                        class="mt-2"
                        :items="quickbooksExpenseAccounts"
                        :loading="loadingAccounts"
                        :disabled="loadingAccounts"
                        label="Quickbooks Account"
                        item-text="accountName"
                        item-value="accountId"
                        v-model="exportAccount"
                        auto-select-first
                        return-object
                        :error-messages="errors.first('account', 'step2')"
                        data-vv-as="Quickbooks Account"
                        data-vv-name="account"
                        data-vv-scope="step2"
                        v-validate="'required'">
                      </v-autocomplete>
                      <v-list-tile-avatar class="pl-3">
                        <v-btn fab small flat color="accent">
                          <v-icon color="accent" @click="refreshQuickbooksAccounts(invoicingCompany.id)">refresh</v-icon>
                        </v-btn>
                      </v-list-tile-avatar>
                    </v-list-tile>
                    <v-divider />
                    <v-list-tile v-if="!createInvoice">
                      <v-list-tile-content>
                        <v-list-tile-title class="font-weight-bold">{{ vendorName }}</v-list-tile-title>
                        <v-list-tile-sub-title>{{ $t('site-title') }} Team Driver or Outside Trucking Company</v-list-tile-sub-title>
                      </v-list-tile-content>
                      <!-- Issue #379. Remove for now -->
                      <!--v-list-tile-avatar>
                        <v-btn fab small flat color="accent">
                          <v-icon color="accent" @click="$confirm('add a new vendor')">add</v-icon>
                        </v-btn>
                      </v-list-tile-avatar-->
                    </v-list-tile>
                    <v-list-tile class="pt-1 pb-3" v-if="!createInvoice">
                      <v-autocomplete class="mt-2"
                                      :items="vendors"
                                      :loading="loadingVendors"
                                      :disabled="loadingVendors"
                                      label="Quickbooks Vendor"
                                      item-text="selectDisplay"
                                      item-value="qbCompanyId"
                                      v-model="qbVendor"
                                      :error-messages="errors.first('vendor', 'step2')"
                                      data-vv-as="Quickbooks Vendor"
                                      data-vv-name="vendor"
                                      data-vv-scope="step2"
                                      v-validate="'required'"
                                      return-object
                      >
                        <template slot="item" slot-scope="slotItems">
                          <div class="title mr-3 mt-1 primary--text">
                            <span v-if="customerCompanyName(slotItems.item)">
                            <v-icon color="primary">business</v-icon>
                            {{ customerCompanyName(slotItems.item) }}
                            </span>
                            <span v-else>
                              <v-icon color="primary">person_outline</v-icon>
                              {{ customerDisplayName(slotItems.item) }}
                            </span>
                          </div>
                          <div class="grey--text caption"
                               v-if="customerCompanyName(slotItems.item) && customerContactName(slotItems.item)">
                            <v-icon color="primary">person_outline</v-icon>
                            <span>{{ customerContactName(slotItems.item) }}</span>
                            <span v-if="customerCompanyName(slotItems.item) !== customerDisplayName(slotItems.item)"
                                  class="font-italic font-weight-light">
                              {{ customerDisplayName(slotItems.item) }}
                            </span>
                          </div>
                        </template>
                      </v-autocomplete>
                      <v-list-tile-avatar class="pl-3">
                        <v-btn fab small flat color="accent" :disabled="loadingVendors">
                          <v-icon color="accent" @click="refreshQuickbooksVendors(invoicingCompany.id)">refresh</v-icon>
                        </v-btn>
                      </v-list-tile-avatar>
                    </v-list-tile>
                    <v-divider></v-divider>
                    <v-list-tile>
                      <v-list-tile-content>
                        <v-list-tile-title class="font-weight-bold" v-if="createInvoice">Customer Payment Terms</v-list-tile-title>
                        <v-list-tile-title class="font-weight-bold" v-else>{{ $t( 'terms.vendor' ) }} Payment Terms</v-list-tile-title>
                      </v-list-tile-content>
                    </v-list-tile>
                    <v-list-tile>
                      <v-select
                        :items="salesTerms"
                        :loading="loadingSalesTerms"
                        :disabled="loadingSalesTerms"
                        :label="createInvoice ? 'Payment Terms' : 'Remitting Terms'"
                        item-text="name"
                        item-value="id"
                        v-model="qbTerms"
                        return-object
                        :error-messages="errors.first('terms', 'step2')"
                        data-vv-as="Payment Terms"
                        data-vv-name="terms"
                        data-vv-scope="step2"
                        v-validate="'required'">
                      </v-select>
                      <v-list-tile-avatar class="pl-3">
                        <v-btn fab small flat color="accent" :disabled="loadingSalesTerms">
                          <v-icon color="accent" @click="refreshQuickbooksPaymentTerms(invoicingCompany.id)">
                            refresh
                          </v-icon>
                        </v-btn>
                      </v-list-tile-avatar>
                    </v-list-tile>
                  </v-list>

                  <v-spacer />

                  <v-btn v-if="createInvoice"
                         large block
                         :disabled="isQuickbooksInfoLoading || errors.any('step2')"
                         :color="btnColor"
                         @click="completeInvoiceStep2()"
                         :loading="loading">
                    <v-icon large color="white" class="pr-2">cloud_upload</v-icon>
                    <v-span v-if="isUsingTax">Select Tax Withholding</v-span>
                    <v-span v-else>Create Direct Invoice</v-span>
                  </v-btn>
                  <v-btn v-if="!createInvoice"
                         block large
                         :color="btnColor"
                         :disabled="errors.any('step2')"
                         @click="setupExportQuickbooksStep3" >
                    <v-icon large color="white" class="pr-2">list</v-icon>
                    Add {{ $t('terms.vendor') }} Discounts
                  </v-btn>
                  <v-btn block flat
                         :color="btnColor" @click="exportQuickbooksStep = 1">
                    Back
                  </v-btn>
                </v-stepper-content>

                <v-stepper-content :step="exportQuickbooksDiscountsStep">

                  <v-list two-line>
                    <v-list-tile>
                      <v-list-tile-content>
                        <template v-if="createInvoice">
                          <v-list-tile-title>Apply Deductions</v-list-tile-title>
                          <v-list-tile-sub-title>from gross payment in expense accounts</v-list-tile-sub-title>
                        </template>
                        <template v-else>
                          <v-list-tile-title>Apply Vendor Discounts</v-list-tile-title>
                          <v-list-tile-sub-title>to income accounts</v-list-tile-sub-title>
                        </template>
                      </v-list-tile-content>
                      <v-list-tile-action>
                        <v-btn fab small color="accent" @click="addDiscount">
                          <v-icon color="white">add</v-icon>
                        </v-btn>
                      </v-list-tile-action>
                    </v-list-tile>

                    <v-list-group v-for="(discount, index) in discounts"
                                  :key="`discounts_group_${index}`"
                                  :value="discount.open">
                      <v-list-tile slot="activator">
                        <v-list-tile-content>
                          <v-list-tile-title>{{ discount.description }}</v-list-tile-title>
                          <v-list-tile-sub-title>{{ discount.account.accountName }}</v-list-tile-sub-title>
                          <v-list-tile-sub-title>{{ discountSubtitle(discount) }}</v-list-tile-sub-title>
                        </v-list-tile-content>
                      </v-list-tile>
                      <v-list-tile>
                        <v-list-tile-action>
                          <v-btn color="accent" fab small @click="removeDiscount(discount)">
                            <v-icon color="white">cancel</v-icon>
                          </v-btn>
                        </v-list-tile-action>
                        <v-text-field v-model="discount.description" label="Discount Description" />
                      </v-list-tile>
                      <v-list-tile>
                        <v-autocomplete solo class="mt-2"
                                        :items="quickbooksDiscountAccounts"
                                        :loading="loadingAccounts"
                                        :disabled="loadingAccounts"
                                        label="Quickbooks Account"
                                        item-text="accountName"
                                        item-value="accountId"
                                        v-model="discount.account"
                                        return-object
                        >
                        </v-autocomplete>

                      </v-list-tile>
                      <v-list-tile>
                        <span v-if="!discount.percent">$</span>
                        <v-float-field v-model="discount.amount"
                                       label="Discount Amount"/>
                        <span class="pr-2" v-if="discount.percent">%</span>
                        <v-checkbox v-model="discount.percent" label="Percent" />
                      </v-list-tile>
                    </v-list-group>
                  </v-list>

                  <v-spacer />

                  <v-btn v-if="!createInvoice"
                         large block color="accent"
                         :disabled="isQuickbooksInfoLoading || errors.any('step3')"
                         :loading="loading"
                         @click="completeInvoiceStep3()">
                    <v-icon large color="white" class="pr-2">cloud_upload</v-icon>
                    <v-span v-if="isUsingTax">Select Tax Withholding</v-span>
                    <v-span v-else>Export {{ $t( 'terms.billable-expense' ) }}</v-span>
                  </v-btn>
                  <v-btn block flat
                         :color="btnColor"
                         @click="exportQuickbooksStep = 2">
                    Back
                  </v-btn>
                </v-stepper-content>

                <v-stepper-content :step="exportQuickbooksTaxStep">

                  <v-list two-line>
                    <v-list-tile v-if="createInvoice">
                      <v-list-tile-content>
                        <v-list-tile-title class="font-weight-bold">Sales Tax Code</v-list-tile-title>
                        <v-list-tile-sub-title>invoice sales line item tax</v-list-tile-sub-title>
                      </v-list-tile-content>
                    </v-list-tile>
                    <v-list-tile v-if="!createInvoice">
                      <v-list-tile-content>
                        <v-list-tile-title class="font-weight-bold">Purchases Tax Code</v-list-tile-title>
                        <v-list-tile-sub-title>bill / purchase line item tax</v-list-tile-sub-title>
                      </v-list-tile-content>
                    </v-list-tile>

                    <v-list-tile class="pt-1">
                      <v-autocomplete
                        class="mt-2"
                        :items="taxCodes"
                        :loading="loadingTaxCodes"
                        :disabled="loadingTaxCodes"
                        label="Quickbooks Tax Code"
                        item-text="name"
                        item-value="id"
                        v-model="taxCode"
                        return-object
                        :error-messages="errors.first('taxCode', 'step4')"
                        data-vv-as="Quickbooks Tax Code"
                        data-vv-name="taxCode"
                        data-vv-scope="step4"
                        v-validate="'required'"
                      >
                      </v-autocomplete>
                      <v-list-tile-avatar class="pl-3">
                        <v-btn fab small flat color="accent" :disabled="loadingTaxCodes">
                          <v-icon color="accent" @click="refreshQuickbooksTaxCodes(invoicingCompany.id)">refresh</v-icon>
                        </v-btn>
                      </v-list-tile-avatar>
                    </v-list-tile>

                    <v-divider />
                  </v-list>

                  <v-spacer/>

                  <v-btn large block
                         :disabled="isQuickbooksInfoLoading || errors.any('step4')"
                         :loading="loading"
                         :color="btnColor"
                         @click="exportIntoQuickbooks()">
                    <v-icon large color="white" class="pr-2">cloud_upload</v-icon>
                    <span v-if="createInvoice">Export Invoice</span>
                    <span v-else>Export {{ $t( 'terms.billable-expense' ) }}</span>
                  </v-btn>
                  <v-btn block flat
                         :color="btnColor"
                         @click="exportQuickbooksStep = exportQuickbooksTaxStep - 1">
                    Back
                  </v-btn>
                </v-stepper-content>
              </v-stepper-items>
            </v-stepper>
          </div>

        </v-layout>
      </v-card>
    </v-dialog>

    <v-badge right overlap
             color="transparent">
      <template slot="badge">
        <v-icon color="green darken-1">
          new_releases
        </v-icon>
      </template>
      <v-quickbooks-btn v-if="showButton"
                        :disabled="disabled"
                        :loading="loading"
                        @click="openQuickbooksModal"/>
      <v-quickbooks-btn v-else
                        :loading="loading"
                        @click="openInfoModal"/>
    </v-badge>
  </v-layout>
</template>

<script>
import _ from 'lodash';
import * as Settlements from '@/types/models/settlement';

import API from '@/api/index';

import FeatureFlagsMixin from '@/mixins/featureFlagMixin';
import QuickbooksMixin from '@/mixins/quickbooksMixin';
import BookedLoadMixin from '@/mixins/bookedLoadMixin';
import LoadTransactionsMixin from '@/mixins/loadTransactionsMixin';
import VFloatField from '@/components/VFloatField';
import VLoadTransactionHistories from '@/components/InfoWidgets/LoadTransactionHistories';

import VQuickbooksBtn from './QuickbooksButton';
import InfoDialog from './InfoDialog';

export default {
  name: 'VQuickbooksBillingBtn',
  mixins: [
    BookedLoadMixin,
    FeatureFlagsMixin,
    LoadTransactionsMixin,
    QuickbooksMixin,
  ],
  components: {
    InfoDialog,
    VFloatField,
    VQuickbooksBtn,
    VLoadTransactionHistories,
  },
  props: {
    /* deprecated soon */
    settlement: {
      type: Object,
      default: null,
    },
    transaction: {
      type: Object,
      default: null,
    },
    invoicingCompany: {
      type: Object,
      required: true,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      showInvoiceDialog: false,
      showInfoDialog: false,
      showBillableDialog: false,
      showCustomerDialog: false,
      selectedInvoiceNumber: null,
      customInvoiceNumber: null,
      invoicedAmount: null,
      invoiceOptions: [],

      qbCustomer: null,
      qbVendor: null,
      qbSalesItem: null,
      exportAccount: null,
      discounts: [],

      qbTerms: null,
      taxCode: { id: null },

      createInvoice: null,
      exportQuickbooksStep: null,
      showTransactionHistory: false,
    };
  },
  computed: {
    _() {
      return _;
    },
    btnColor() {
      return this.createInvoice ? 'primary' : 'accent';
    },
    showButton() {
      return this.isQuickbooksConnected(this.invoicingCompany) &&
        (this.currentUserHasCreateSettlementInvoicePermission(this.settlement || {}) ||
         this.currentUserHasCreateTransactionInvoicePermission(this.transaction || {}));
    },
    quickbooksExpenseAccounts() {
      return _.filter(this.loadedAccounts, a => a.classification === 'EXPENSE');
    },
    quickbooksIncomeAccounts() {
      return _.filter(this.loadedAccounts, a => a.classification === 'REVENUE');
    },
    quickbooksDiscountAccounts() {
      return this.createInvoice ? this.quickbooksExpenseAccounts : this.quickbooksIncomeAccounts;
    },
    exportQuickbooksDiscountsStep() {
      if (this.createInvoice) {
        return 100;
      }
      return 3;
    },
    exportQuickbooksTaxStep() {
      if (this.createInvoice) {
        return 3;
      }
      return 4;
    },
    quickbooksExportTitle() {
      if (this.createInvoice == null) {
        return 'Export Into Quickbooks';
      } else if (this.createInvoice) {
        return 'Create Quickbooks Invoice';
      } else {
        return 'Create Quickbooks Billable Expense';
      }
    },
    mappedCustomer() {
      if (this.settlement) {
        const customerId = parseInt(this.txnCustomerCompanyId, 10);
        return _.find(this.customers, c => parseInt(c.ltnCompanyId, 10) === customerId);
      }

      return this.transactionQbCustomer(this.transaction, this.invoicingCompany);
    },
    isSelfDispatched() {
      if (this.settlement) {
        return this.settlement.truckingCompanyId === this.settlement.ownerCompanyId;
      }

      return this.transactionLoadIsDirectDispatched(this.transaction);
    },
    accountVendorId() {
      return _.get(this.transaction, 'vendor.accountVendorId');
    },
    isVendorAssigned() {
      return !!this.accountVendorId;
    },
    loadOwner() {
      return this.transactionLoadOwner(this.transaction);
    },
    truckingCompany() {
      return this.transactionTruckingCompany(this.transaction);
    },
    vendorName() {
      // first off, this only makes sense if we are creating a vendor expense
      if (this.createInvoice) {
        return null;
      }

      let vendorName;
      if (this.isSelfDispatched) {
        // here, we dispatched to ourself.  Is there a driver on the load?
        const itemVendorName = _.get(this.settlement, 'driverName');
        vendorName = _.get(this.transaction, 'load.delivery.truckingCompanyDriverName', itemVendorName);
      } else if (this.isVendorAssigned) {
        vendorName = _.get(this.transaction, 'vendor.companyName');
      } else {
        // here, load is dispatched
        const itemVendorName = _.get(this.settlement, 'truckingCompany.name');
        vendorName = _.get(this.truckingCompany, 'name', itemVendorName);
      }

      if (vendorName) {
        return `Vendor (for ${vendorName})`;
      }
      return 'Quickbooks Vendor';
    },

    salesItems() {
      return this.loadingSalesItems ? [] : this.quickbooksSalesItems(this.invoicingCompany.id);
    },
    loadingSalesItems() {
      return this.salesItemsStatus[this.invoicingCompany.id] || false;
    },
    salesTerms() {
      return this.loadingSalesTerms ? [] : this.quickbooksPaymentTerms(this.invoicingCompany.id);
    },
    loadingSalesTerms() {
      return this.salesTermsStatus[this.invoicingCompany.id] || false;
    },
    customers() {
      return this.loadingCustomers ? [] : this.quickbooksCustomers(this.invoicingCompany.id);
    },
    loadingCustomers() {
      return this.customersStatus[this.invoicingCompany.id] || false;
    },
    vendors() {
      return this.loadingVendors ? [] : this.quickbooksVendors(this.invoicingCompany.id);
    },
    isUsingTax() {
      return this.quickbooksCompanyCollectsTax(this.invoicingCompany);
    },
    taxCodes() {
      const codes = this.createInvoice
        ? this.quickbooksSalesTaxCodes(this.invoicingCompany.id)
        : this.quickbooksPurchaseTaxCodes(this.invoicingCompany.id);

      return codes;
    },
    defaultTaxCode() {
      const codes = this.defaultCompanyQuickbooksTaxCodes(this.invoicingCompany);
      return this.createInvoice
        ? codes.sales
        : codes.purchase;
    },
    loadingVendors() {
      return this.vendorsStatus[this.invoicingCompany.id] || false;
    },
    loadedAccounts() {
      return this.loadingAccounts ? [] : this.quickbooksAccounts(this.invoicingCompany.id);
    },
    loadingAccounts() {
      return this.accountsStatus[this.invoicingCompany.id] || false;
    },
    loadingTaxCodes() {
      return this.taxCodesStatus[this.invoicingCompany.id] || false;
    },
    isQuickbooksInfoLoading() {
      return this.isCompanyQuickbooksInfoLoading(this.invoicingCompany.id) || false;
    },
    loadHumanId() {
      return (this.settlement || this.transaction).loadHumanId;
    },
    defaultInvoiceAmount() {
      return (this.transaction || this.settlement).amount;
    },
    billOfLadingNumber() {
      const itemBol = _.get(this.settlement, 'billOfLadingNumber');
      return _.get(this.transaction, 'load.delivery.billOfLadingNumber', itemBol);
    },
    transactionId() {
      if (this.settlement) {
        return this.settlement.id;
      }
      return this.transaction.id;
    },
    txnCustomerCompanyId() {
      if (this.settlement) {
        return Settlements.invoice(this.settlement).billedTo.companyId;
      }

      return this.transaction.customer.companyId;
    },
    txnCustomerCompanyName() {
      if (this.settlement) {
        return Settlements.invoice(this.settlement).billedTo.companyName;
      }

      return this.transaction.customer.companyName;
    },
    txnVendorCompanyId() {
      if (this.settlement) {
        return Settlements.invoice(this.settlement).paidTo.companyId;
      }

      return this.transaction.vendor.companyId;
    },
    txnVendorCompanyName() {
      if (this.settlement) {
        return Settlements.invoice(this.settlement).paidTo.companyName;
      }

      return this.transaction.vendor.companyName;
    },
  },
  methods: {
    openBillOfLading() {
      const url = this.transaction.load.billOfLading.link;
      this.$store.dispatch('lightbox/show', url);
    },
    calculateDispatchSpread() {
      if (this.settlement) {
        return this.settlement.dispatchSpread;
      }

      const customerTerms = this.transaction.load.terms.customer;
      const freightTerms = this.transaction.load.terms.freight;

      if (customerTerms && freightTerms) {
        const customerRate = customerTerms.amount;
        const freightRate = freightTerms.amount;

        const spread = customerRate - freightRate;
        return spread;
      }

      return null;
    },
    addDiscount() {
      this.discounts.push({
        description: 'New Discount ' + (this.discounts.length + 1),
        account: { accountId: null, accountName: null },
        amount: null,
        percent: null,
        open: true,
      });
    },
    removeDiscount(discount) {
      this.discounts = _.reject(this.discounts, d => d === discount);
    },
    discountSubtitle(discount) {
      if (discount.amount != null) {
        if (discount.percent) {
          const scalar = this.invoicedAmount * (discount.amount / 100);
          return `${discount.amount}% ($${scalar})`;
        }
        return `$${discount.amount}`;
      }
      return '';
    },
    suggestedVendor() {
      // first off, this only makes sense if we are creating a vendor expense
      if (this.createInvoice) {
        return undefined;
      }

      if (this.qbVendor) {
        return this.qbVendor;
      }

      if (this.isSelfDispatched) {
        const driverUserId = this.settlement ? this.settlement.driverUserId : this.transaction.load.delivery.truckingCompanyDriverUserId;
        if (driverUserId) {
          const userId = parseInt(driverUserId, 10);
          const driverEe = _.find(_.get(this.invoicingCompany, 'employees', []), e => parseInt(e.userId, 10) === userId);

          const vendorId = _.get(driverEe, 'quickbooksVendorId');
          if (vendorId) {
            const driverVendorId = parseInt(vendorId, 10);
            const driverVendor = _.find(this.vendors, v => parseInt(v.qbCompanyId, 10) === driverVendorId);
            return driverVendor;
          }
        }

        return undefined;
      }

      if (this.transaction && this.isBookedLoadVendorAssigned(this.transaction.load)) {
        const vendorId = this.bookedLoadVendorCarrierCompany(this.transaction.load).id;
        const accountVendor = _.find(this.vendors, v => v.qbCompanyId === vendorId);
        return accountVendor;
      }

      if (this.settlement && this.settlement.paidToVendorId) {
        const vendorId = this.settlement.paidToVendorId;
        const accountVendor = _.find(this.vendors, v => v.qbCompanyId === vendorId);
        return accountVendor;
      }

      const truckingCompanyId = this.settlement ? parseInt(this.settlement.truckingCompany.id, 10) : parseInt(this.truckingCompany.id, 10);
      const truckingCompanyVendor = _.find(this.vendors, v => parseInt(v.ltnCompanyId, 10) === truckingCompanyId);
      return truckingCompanyVendor;
    },
    openInfoModal() {
      this.showInfoDialog = true;
    },
    setupInvoiceFlow() {
      this.exportQuickbooksStep = 1;
      this.createInvoice = true;
    },
    setupBillableExpenseFlow() {
      this.exportQuickbooksStep = 1;
      this.selectedInvoiceNumber = this.billOfLadingNumber;
      this.createInvoice = false;
    },
    setupExportQuickbooksStep2() {
      // TODO: setup default values for discounts/fees
      if (this.createInvoice) {
        this.qbTerms = this.defaultCompanyQuickbooksCustomerInvoiceTerms(this.invoicingCompany);
        this.qbSalesItem = this.defaultCompanyQuickbooksInvoiceSalesItem(this.invoicingCompany);
      } else {
        this.exportAccount = this.defaultCompanyQuickbooksTransportationFeeExpenseAccount(this.invoicingCompany);

        this.qbTerms = this.defaultCompanyQuickbooksVendorBillTerms(this.invoicingCompany);

        this.qbVendor = this.suggestedVendor();

        if (this.qbVendor) {
          const vendorTermsId = this.qbVendor.defaultPaymentTermsId;
          const vendorTerms = _.find(this.quickbooksPaymentTerms(this.invoicingCompany.id), t => vendorTermsId && t.id === vendorTermsId);

          if (vendorTerms) {
            this.qbTerms = vendorTerms;
          }
        }
      }

      this.$validator.validateAll('step1').then((valid) => {
        if (valid) {
          this.exportQuickbooksStep = 2;
        }
      });
    },
    isStep2Valid() {
      return this.$validator.validateAll('step2');
    },
    async setupExportQuickbooksStep3() {
      this.taxCode = this.defaultTaxCode;
      if (this.createInvoice) {
      } else {
      }
      const isValid = await this.isStep2Valid();

      if (isValid) {
        this.exportQuickbooksStep = 3;
      }
    },
    openQuickbooksModal() {
      this.exportQuickbooksStep = null;
      this.createInvoice = null;
      this.invoicedAmount = this.defaultInvoiceAmount;
      this.selectedInvoiceNumber = this.loadHumanId;
      this.customInvoiceNumber = null;
      this.discounts = []; // TODO: initialize based on the suggestion from the settlement item
      this.qbSalesItem = null;

      if (this.mappedCustomer) {
        this.qbCustomer = this.mappedCustomer;
      } else {
        this.qbCustomer = null;
      }

      this.invoiceOptions = [];

      if (this.billOfLadingNumber) {
        this.invoiceOptions.push({ label: `${this.$t('terms.bill-of-lading')} Number: ${this.billOfLadingNumber}`, value: this.billOfLadingNumber });
      }

      this.invoiceOptions.push(
        { label: `Load Number: ${this.loadHumanId}`, value: this.loadHumanId },
      );

      this.invoiceOptions.push({ label: 'Let Quickbooks Create Document Number', value: null });

      this.showInvoiceDialog = true;
      console.log('show');
      console.log(this.transaction);
    },
    completeInvoiceStep2() {
      if (this.isUsingTax) {
        this.taxCode = this.defaultTaxCode;
        this.exportQuickbooksStep = 3;
      } else {
        // here is when we don't use tax
        this.exportIntoQuickbooks();
      }
    },
    completeInvoiceStep3() {
      if (this.isUsingTax) {
        this.taxCode = this.defaultTaxCode;
        this.exportQuickbooksStep = 4;
      } else {
        // here is when we don't use tax
        this.exportIntoQuickbooks();
      }
    },
    async exportIntoQuickbooks() {
      try {
        this.loading = true;

        let documentNumber = this.selectedInvoiceNumber;
        if (documentNumber === '__other') {
          documentNumber = this.customInvoiceNumber;
          if (documentNumber == null) {
            await this.$confirm('Must provide document number');
            return;
          }
        }

        if (this.invoicedAmount == null) {
          await this.$confirm('Must provide invoice amount.');
          return;
        }

        const billableExpense = this.createInvoice ? null : {
          vendorId: this.qbVendor.qbCompanyId,
          vendorName: this.qbVendor.selectDisplay,
          expenseAccountId: this.exportAccount.accountId,
          expenseAccountName: this.exportAccount.selectDisplay,
          purchaseTaxCodeRefId: this.taxCode.id,

          vendorDiscounts: _.map(this.discounts, d => ({
            description: d.description,
            accountId: d.account.accountId,
            accountName: d.accountName,
            percent: d.percent ? d.amount : null,
            amount: d.percent ? null : d.amount,
            storeWithTemplate: !(d.isDispatchSpread || false),
          })),
        };

        const invoice = !this.createInvoice ? null : {
          salesItemId: this.qbSalesItem.itemId,
          salesItemName: this.qbSalesItem.description,
          salesTaxCodeRefId: this.taxCode.id,
        };

        const args = {
          companyId: this.txnVendorCompanyId,
          body: {
            settlementId: _.get(this.settlement, 'id', null),
            transactionId: _.get(this.transaction, 'id', null),
            qbCustomerId: this.qbCustomer.qbCompanyId,
            invoicedAmount: this.invoicedAmount,
            documentNumber,
            salesTermsId: this.qbTerms.id,
            salesTermsName: this.qbTerms.name,
            billableExpense,
            invoice,
          },
        };

        let created;
        if (this.createInvoice) {
          created = await API.quickBooks.exportInvoice(args);
        } else {
          created = await API.quickBooks.exportBillableExpense(args);
        }
        const document = created.data;
        this.$store.dispatch('applications/setSuccessNotification', {
          message: `Document ${document.docNumber} Created`,
        });

        if (this.txnCustomerCompanyId && !this.mappedCustomer) {
          this.refreshQuickbooksCustomers();
        }

        // when suggestedVendor is absent or different than qbVendor, we'll map it server side. let's pull the changes
        if (billableExpense) {
          const suggestedVendor = this.suggestedVendor();
          const knownVendor = suggestedVendor && suggestedVendor.qbCompanyId === billableExpense.vendorId;
          if (!knownVendor) {
            this.refreshQuickbooksVendors(this.invoicingCompany.id);
          }
        }

        this.showInvoiceDialog = false;

        this.$emit('invoiceCreated');
      } catch (err) {
        this.$store.dispatch('applications/setErrorNotification', {
          message: `QB Export Request Failed. Try again. ${err.message}`,
        });
      } finally {
        this.loading = false;
      }
    },
    resetState() {
      const codes = this.defaultCompanyQuickbooksTaxCodes(this.invoicingCompany);
      if (this.createInvoice) {
        this.taxCode = codes.sales;
      } else {
        this.taxCode = codes.purchase;
      }

      this.qbVendor = null;
      this.selectedInvoiceNumber = null;
      this.customInvoiceNumber = null;
      this.invoicedAmount = null;
      this.invoiceOptions = [];

      this.qbCustomer = null;
      this.qbVendor = null;
      this.qbSalesItem = null;
      this.exportAccount = null;
      this.discounts = [];

      this.qbTerms = null;

      this.showInvoiceDialog = false;
    },
  },
  watch: {
    qbVendor(newVendor, oldVendor) {
      this.discounts = [];

      const dispatchSpread = this.calculateDispatchSpread();
      const dispatchFeeAccount = this.defaultCompanyQuickbooksDispatchFeeIncomeAccount(this.invoicingCompany);
      if (dispatchSpread != null && dispatchFeeAccount) {
        this.discounts.push({
          description: 'Dispatch Fee',
          account: dispatchFeeAccount,
          amount: dispatchSpread,
          percent: null,
          open: true,
          isDispatchSpread: true,
        });
      }

      if (newVendor) {
        const termsId = _.get(newVendor, 'defaultPaymentTermsId');
        const vendorTerms = _.find(this.salesTerms, t => t.id === termsId);
        this.qbTerms = vendorTerms || this.qbTerms;

        const accountsById = _.groupBy(this.loadedAccounts, a => a.accountId);

        if (newVendor.vendorDiscounts) {
          _.forEach(newVendor.vendorDiscounts, vd => {
            const discountAccount = accountsById[vd.accountId];

            if (discountAccount) {
              this.discounts.push({
                description: vd.description,
                account: discountAccount[0],
                amount: vd.amount || vd.percent,
                percent: !!(vd.percent || false),
                open: true,
              });
            }
          });
        }
      } else {
        const trailerLease = this.defaultCompanyQuickbooksTrailerLeaseFeeIncomeAccount(this.invoicingCompany);
        if (this.isSelfDispatched && trailerLease) {
          this.discounts.push({
            description: 'Trailer Lease',
            account: trailerLease,
            amount: 15,
            percent: true,
            open: true,
          });
        }
      }
    },
    settlement(newVal) {
      this.resetState();
    },
    transaction(newVal) {
      this.resetState();
    },
  },
  mounted() {
    const codes = this.defaultCompanyQuickbooksTaxCodes(this.invoicingCompany);
    if (this.createInvoice) {
      this.taxCode = codes.sales;
    } else {
      this.taxCode = codes.purchase;
    }
  },
  created() {
    if (this.settlement && this.transaction) {
      throw new TypeError('Must specify exactly one of settlement or transaction to the Quickbooks button');
    }

    if (this.settlement || this.transaction) {
      this.invoicedAmount = this.defaultInvoiceAmount;
      // If ltn_admin load qb data
      if (this.isLtnAdmin) {
        this.loadCompanyQuickbooksInfo(this.invoicingCompany.id);
      }
    } else {
      throw new TypeError('Must specify exactly one of settlement or transaction to the Quickbooks button');
    }
  },
};
</script>



// WEBPACK FOOTER //
// src/components/qb/QuickbooksBillingButton.vue